<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";

// The app name with the sleuth's magnifying glass after it. Used wherever Velma is written as
// a heading. It inherits the heading's font, colour and size, so every measurement below is in
// em and the same component fits the header, the sign-in card and the chat empty state.
//
// With `sweep`, the glass plays once per browser tab. It appears left of the name, glides
// across the letters and magnifies them as it passes, then shrinks and settles into the static
// mark. The lens holds a second copy of the text at a larger size, slid the opposite way, so
// the letter under the lens lines up with the letter behind it. Only the header and the sign-in
// card ask for the sweep. The chat empty state's "Ask Velma" is a tagline, not the logo, and
// stays still. Five quick clicks on a sweeping lockup replay it on demand, with no delay.
const props = withDefaults(defineProps<{ text?: string; sweep?: boolean }>(), {
  text: "Velma",
  sweep: false,
});

/** Once per tab, not once per mount. A mount-only guard replays it at every step of arriving.
 *  The sign-in card sweeps, the header sweeps again seconds later when the shell mounts, and
 *  every reload sweeps again. sessionStorage lives as long as the tab, which is what "once"
 *  means here. localStorage would silence it forever after the first visit. Read and written
 *  in the same tick, so two lockups mounting together can't both claim it. */
const PLAYED_KEY = "velma.wordmarkSwept";

function claimSweep(): boolean {
  try {
    if (sessionStorage.getItem(PLAYED_KEY)) return false;
    sessionStorage.setItem(PLAYED_KEY, "1");
    return true;
  } catch {
    // Storage is blocked, as in some private modes. Skipping beats a replay on every mount.
    return false;
  }
}

const sweeping = ref(false);

/** Seconds before the glass appears. One second on page load, so the page has settled before
 *  anything moves. Zero on a replay, which someone just asked for by clicking. */
const delay = ref("1s");

onMounted(() => {
  if (!props.sweep) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (!claimSweep()) return;
  sweeping.value = true;
});

/** The replay easter egg: five clicks on the logo within a second and a half of each other.
 *  Counts only on lockups that sweep, so the chat tagline stays inert. Skips the once-per-tab
 *  guard and the reduced-motion check on purpose. Clicking five times is a request. */
const REPLAY_CLICKS = 5;
const REPLAY_GAP_MS = 1500;
let clicks = 0;
let lastClickAt = 0;

async function onClick() {
  if (!props.sweep) return;
  const now = Date.now();
  clicks = now - lastClickAt > REPLAY_GAP_MS ? 1 : clicks + 1;
  lastClickAt = now;
  if (clicks < REPLAY_CLICKS) return;
  clicks = 0;
  // Drop the class for a frame so the browser restarts the animations from their first
  // keyframe. Setting it while it is already set would do nothing.
  sweeping.value = false;
  await nextTick();
  delay.value = "0s";
  sweeping.value = true;
}

/** Only the wrapper's own sweep ends the animated state, not the appear fade or the lens text's
 *  counter-slide. The sweep's last frame equals the static rules, so nothing jumps on hand-off. */
function onAnimationEnd(event: AnimationEvent) {
  if (event.animationName.startsWith("sweep")) sweeping.value = false;
}
</script>

<template>
  <span class="lockup" :class="{ sweeping }" :style="{ '--sweep-delay': delay }" @click="onClick">
    <span class="word">{{ text }}</span>
    <!-- Decorative: the word next to it already names the app. -->
    <span class="glass" aria-hidden="true" @animationend.self="onAnimationEnd">
      <span class="lens">
        <span v-if="sweeping" class="lens-text">{{ text }}</span>
        <span class="glint"></span>
      </span>
      <span class="handle"></span>
    </span>
  </span>
</template>

<style scoped>
/* Geometry shared by the rest state and the keyframes, in em of the heading.

   --lens is the ring's outer diameter at rest and --rim its stroke. Together they are about
   the size of the icon-font glass this replaced, so the header's layout didn't move.
   --gap is the space between the last letter and the ring.
   --lift raises the ring a little above the text's centre line. The handle hangs off the
   ring's lower right, so lifting the ring puts the mark as a whole on that line.
   --zoom is how much bigger the glass is while sweeping.
   --mag is how much the lens magnifies the text at that size. */
.lockup {
  --lens: 0.46em;
  --rim: 0.06em;
  --gap: 0.2em;
  --lift: 0.08em;
  --zoom: 2.9;
  --mag: 1.3;
  /* How far left of the first letter the glass appears. */
  --lead: 0.5em;

  /* Where the ring's centre rests: past the text, then the gap, then its own radius. */
  --rest-x: calc(100% + var(--gap) + var(--lens) / 2);
  --rest-y: calc(50% - var(--lift));

  position: relative;
  display: inline-block;
  line-height: 1;
  white-space: nowrap;
  /* The glass is out of flow. Reserve room for the ring and the handle's reach so the
     heading's neighbours don't sit on top of it. */
  margin-inline-end: calc(var(--gap) + var(--lens) + 0.24em);
}

.word {
  display: inline-block;
}

.glass {
  position: absolute;
  left: var(--rest-x);
  top: var(--rest-y);
  inline-size: var(--lens);
  block-size: var(--lens);
  transform: translate(-50%, -50%) scale(1);
  color: var(--wa-color-brand-fill-loud);
}

/* The ring. Everything inside is clipped to the circle. That clipping is what makes the second
   copy of the text read as a view through the lens instead of text stacked on text. */
.lens {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: var(--rim) solid currentColor;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 50%, transparent 55%, rgb(255 255 255 / 0.35) 100%),
    rgb(255 255 255 / 0.12);
  box-shadow:
    0 0.02em 0.06em rgb(0 0 0 / 0.18),
    inset 0 0 0.03em rgb(255 255 255 / 0.7);
}

/* A highlight across the upper-left of the glass, on top of whatever the lens shows. */
.glint {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(
    circle at 32% 28%,
    rgb(255 255 255 / 0.8) 0,
    rgb(255 255 255 / 0.25) 22%,
    rgb(255 255 255 / 0) 48%
  );
}

/* The handle leaves the rim at the circle's 4 o'clock point, centre plus r·cos45° and r·sin45°,
   and runs outward at 45°. Its inner end is tucked a stroke's width under the rim to hide the
   seam at the join. */
.handle {
  --length: 0.28em;
  --thickness: 0.09em;
  position: absolute;
  left: calc(50% + var(--lens) * 0.3536 - var(--rim) * 0.7);
  top: calc(50% + var(--lens) * 0.3536 - var(--thickness) / 2 - var(--rim) * 0.7);
  inline-size: var(--length);
  block-size: var(--thickness);
  border-radius: calc(var(--thickness) / 2);
  transform-origin: left center;
  transform: rotate(45deg);
  background: linear-gradient(
    to right,
    currentColor 40%,
    color-mix(in srgb, currentColor 78%, black)
  );
  box-shadow: 0 0.02em 0.04em rgb(0 0 0 / 0.2);
}

/* The magnified copy of the text. It should be --mag times the heading, but it lives inside a
   wrapper that the sweep scales by --zoom, so its own font-size is --mag / --zoom. It starts
   centred on the lens and slides left as the glass slides right. The keyframes below keep the
   two in step. */
.lens-text {
  position: absolute;
  left: 50%;
  top: 50%;
  font-size: calc(var(--mag) / var(--zoom) * 1em);
  line-height: 1;
  white-space: nowrap;
  color: var(--wa-color-text-normal);
  transform: translate(0, -50%);
}

/* ---- The sweep ----

   Three animations share one delay (--sweep-delay, set from script) and one duration. When
   the sweep ends, onAnimationEnd hands off to the static rules above.

   appear   fades the glass in over the first third of a second.
   sweep    moves the glass from just left of the first letter to its rest position, reached
            72% of the way through, then shrinks it from --zoom to 1 and lifts it to --rest-y.
   counter  slides the magnified text the opposite way, so the letter behind the lens's centre
            is always the letter the lens shows.

   sweep and counter stay in step because they have the same keyframe stops and the same
   easing. Between two shared stops each one is linear in the same progress value.

   The counter's maths. Call the text width W and the sweep's progress p, from 0 to 1. The
   glass centre sits at x = p·W + e(p), where e runs from -(--lead) before the text to
   --gap + --lens/2, its rest offset. The lens shows the point at x when the copy is shifted by
   -(--mag/--zoom)·x in the wrapper's own coordinates. The copy is (--mag/--zoom)·W wide, so the
   p·W part is translateX(-p·100%) of its own width. The e(p) part is a length. The copy's
   font-size is (--mag/--zoom) of the heading's, so in the copy's own em it is just -e(p), the
   sweep's --lead, --gap and --lens tokens with the sign flipped. This works because a custom
   property carries its em unit as text, so var(--lens) inside .lens-text resolves against the
   smaller font-size. */
.sweeping .glass {
  animation:
    appear 0.35s ease-out var(--sweep-delay) both,
    sweep 2.2s var(--sweep-delay) both;
}

.sweeping .lens-text {
  animation: counter 2.2s var(--sweep-delay) both;
}

@keyframes appear {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes sweep {
  0% {
    left: calc(-1 * var(--lead));
    top: 50%;
    transform: translate(-50%, -50%) scale(var(--zoom));
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  72% {
    left: var(--rest-x);
    top: 50%;
    transform: translate(-50%, -50%) scale(var(--zoom));
    animation-timing-function: cubic-bezier(0.34, 1.4, 0.64, 1);
  }
  100% {
    left: var(--rest-x);
    top: var(--rest-y);
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes counter {
  0% {
    transform: translate(var(--lead), -50%);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  72%,
  100% {
    transform: translate(calc(-100% - var(--gap) - var(--lens) / 2), -50%);
  }
}
</style>
